<template>
  <div>
    <svg
      class="map-chart"
      ref="chartSVG"
      viewBox="0 0 546.5 198.109"
      preserveAspectRatio="none"
    >
      <path class="target" :d="targetPoints" />
      <path :style="{ fill: mildHypoColor }" :d="greyHighlightsPoints" />
      <path :style="{ fill: severeHypoColor }" :d="darkGreyHighlightsPoints" />
      <path class="green" :d="greenLinePoints" />
      <path class="dash" :d="dashPoints" />
    </svg>
  </div>
</template>

<script>
import { markersMixin } from '@/components/MarkersMixin.js'

export default {
  name: 'TargetPlot',
  data() {
    return {
      canvasWidth: 546.5,
      canvasHeight: 198.109,
      greenLinePoints: '',
      greyHighlightsPoints: '',
      darkGreyHighlightsPoints: ''
    }
  },
  mixins: [markersMixin],
  props: {
    signal: {
      type: Array,
      required: true
    },
    markers: {
      type: Array,
      required: false
    },
    xMax: {
      type: Number,
      required: true
    },
    xMin: {
      type: Number,
      required: true
    },
    yMax: {
      type: Number,
      required: true
    },
    yMin: {
      type: Number,
      required: true
    },
    targetMin: {
      type: Number,
      required: true
    },
    targetMax: {
      type: Number,
      required: true
    },
    mildHypoColor: {
      type: String,
      required: true
    },
    severeHypoColor: {
      type: String,
      required: true
    }
  },
  watch: {
    signal: {
      handler() {
        this.renderSignal()
      },
      deep: true
    },
    yMin() {
      this.renderSignal()
    },
    yMax() {
      this.renderSignal()
    }
  },
  methods: {
    renderSignal() {
      if (!this.signal || this.signal.length === 0) {
        return
      }
      let thresh = [55, this.targetMin]
      let downsampSignal = this.downsample(this.signal, 500)
      let interpSignal = this.interpolateSignal(downsampSignal, thresh)
      let filledSignal = this.fillNaNs(interpSignal)
      let scaledSignal = this.scaleSignal(filledSignal)
      if (filledSignal.length === 0) {
        filledSignal = [
          [0, NaN],
          [1, NaN]
        ]
      }

      this.greenLinePoints = this.signalToPoints(scaledSignal)
      let greyThreshold =
        this.canvasHeight -
        this.canvasHeight *
          ((this.targetMin - this.yMin) / (this.yMax - this.yMin))
      let darkGreyThreshold =
        this.canvasHeight -
        this.canvasHeight * ((55 - this.yMin) / (this.yMax - this.yMin))
      this.greyHighlightsPoints = this.signalToHighlightPoints(
        scaledSignal,
        greyThreshold
      )
      this.darkGreyHighlightsPoints = this.signalToHighlightPoints(
        scaledSignal,
        darkGreyThreshold
      )
      this.markersUpdate(this.$refs['chartSVG'], this.markers)
    },
    interpolateSignal(signal, thresholds) {
      var signal = signal.slice(0)
      for (const thresh of thresholds) {
        for (let i = 0; i < signal.length - 1; i++) {
          if (signal[i][1] > thresh && signal[i + 1][1] < thresh) {
            signal.splice(
              i + 1,
              0,
              this.interpolatePoint(signal[i], signal[i + 1], thresh)
            )
            i++
          } else if (signal[i][1] < thresh && signal[i + 1][1] > thresh) {
            signal.splice(
              i + 1,
              0,
              this.interpolatePoint(signal[i], signal[i + 1], thresh)
            )
            i++
          }
        }
      }
      return signal
    },
    fillNaNs(signal) {
      if (signal.length < 2) {
        return signal
      }
      for (let i = 0; i < signal.length - 1; i++) {
        if (!Number.isNaN(signal[i][1]) && Number.isNaN(signal[i + 1][1])) {
          signal.splice(i + 1, 0, [signal[i][0], NaN])
          i++
        } else if (
          Number.isNaN(signal[i][1]) &&
          !Number.isNaN(signal[i + 1][1])
        ) {
          signal.splice(i + 1, 0, [signal[i + 1][0], NaN])
          i++
        }
      }
      return signal
    },
    scaleSignal(signal) {
      return signal.map(el => [
        this.canvasWidth * ((el[0] - this.xMin) / (this.xMax - this.xMin)),
        this.canvasHeight -
          this.canvasHeight * ((el[1] - this.yMin) / (this.yMax - this.yMin))
      ])
    },
    downsample(signal, nPoints) {
      if (signal.length <= nPoints) {
        return signal
      }
      let xPerPoint = (signal[signal.length - 1][0] - signal[0][0]) / nPoints
      let windowEnd = signal[0][0] + xPerPoint
      let downsampSig = []
      let xMean = 0
      let yMean = 0
      let count = 0
      downsampSig.push(signal[0])
      for (let i = 1; i < signal.length - 1; i++) {
        xMean += signal[i][0]
        yMean += signal[i][1]
        count++
        if (signal[i][0] > windowEnd) {
          downsampSig.push([xMean / count, yMean / count])
          xMean = yMean = count = 0
          windowEnd = (Math.floor(signal[i][0] / xPerPoint) + 1) * xPerPoint
        }
      }
      downsampSig.push(signal[signal.length - 1])
      return downsampSig
    },
    interpolatePoint(left, right, thresh) {
      let x =
        left[0] +
        ((thresh - left[1]) / (right[1] - left[1])) * (right[0] - left[0])
      return [x, thresh]
    },
    signalToPoints(signal) {
      let points = ''
      let firstPoint = true
      for (const point of signal) {
        if (isNaN(point[1])) {
          firstPoint = true
        } else if (firstPoint) {
          points += `M${point[0]} ${point[1]} `
          firstPoint = false
        } else {
          points += `L${point[0]} ${point[1]} `
        }
      }
      return points
    },
    signalToHighlightPoints(signal, threshold) {
      let points = ''
      let firstPoint = true
      let prevPoint = null
      for (const point of signal) {
        if (isNaN(point[1]) || point[1] < threshold) {
          firstPoint = true
          if (prevPoint != null) {
            points += `L${prevPoint[0]} ${threshold} `
          }
          prevPoint = null
        } else if (firstPoint) {
          points += `M${point[0]} ${threshold} `
          points += `L${point[0]} ${point[1]} `
          firstPoint = false
          prevPoint = point
        } else {
          points += `L${point[0]} ${point[1]} `
          prevPoint = point
        }
      }
      // Handle last point under-threshold
      let lastPoint = signal[signal.length - 1]
      if (lastPoint[1] > threshold) {
        points += `L${lastPoint[0]} ${threshold} `
      }
      return points
    }
  },
  mounted() {
    this.renderSignal()
  },
  computed: {
    targetPoints() {
      let upperY =
        this.canvasHeight -
        ((this.targetMax - this.yMin) / (this.yMax - this.yMin)) *
          this.canvasHeight
      let lowerY =
        this.canvasHeight -
        ((this.targetMin - this.yMin) / (this.yMax - this.yMin)) *
          this.canvasHeight
      return `M0 ${lowerY} L${this.canvasWidth} ${lowerY} L${this.canvasWidth} ${upperY} L0 ${upperY} Z`
    },
    dashPoints() {
      let y =
        this.canvasHeight -
        ((55 - this.yMin) / (this.yMax - this.yMin)) * this.canvasHeight
      return `M0 ${y} L 546.5 ${y}`
    }
  }
}
</script>

<style scoped>
.map-chart {
  position: absolute;
  display: block;
  height: 100%;
  width: 100%;
  background: #eeeeee;
}
path {
  stroke-width: 0.4;
}
.target {
  stroke: none;
  fill: #c5eecd;
}
.green {
  stroke: #00413b;
  stroke-width: 3;
  fill: none;
}
.dash {
  stroke: #818181;
  stroke-dasharray: 3.8 3.8;
  stroke-width: 1.5;
  fill: none;
}
</style>
